/**
 * Service de recherche de villes françaises via l'API publique
 * geo.api.gouv.fr (Karim 2026-06-25).
 *
 * - Pas d'auth, illimitée, ultra rapide (CDN public, ~30ms).
 * - Search par NOM partiel OU par code postal (5 chiffres).
 * - Retourne nom + code postal principal + slug (pour URL Donoor).
 *
 * Slug = CLÉ DE VILLE de l'annuaire (lot 4, 2026-09-27) : construite depuis le
 * code INSEE de la commune (`departementDepuisCodes` + `slugVille`, règle
 * unique de @donoor/utils, miroir de la base) → « sainte-luce-972 » pour la
 * Martinique, « saint-denis » (93) ≠ « saint-denis-974 » (La Réunion). Le
 * filtre ville de /recherche retrouve ainsi exactement les associations de
 * CETTE commune, homonymes exclus.
 *
 * Doc : https://geo.api.gouv.fr/decoupage-administratif/communes
 */
import { departementDepuisCodes, slugVille, territoireDepuisCodes } from "@donoor/utils";
import { nomVilleAnnuaire } from "#shared/utils/villeAnnuaire";

export interface CitySuggestion {
  /**
   * Nom de la commune, territoire précisé hors métropole (« Lyon »,
   * « Sainte-Luce (Martinique) ») — même libellé que les pages ville.
   */
  name: string;
  /** Clé de ville de l'annuaire (« lyon », « sainte-luce-972 »). */
  slug: string;
  /** Code postal principal (la commune peut en avoir plusieurs ; on prend
   *  le 1er retourné, ou celui qui matche la query si elle est numérique). */
  postalCode: string;
  /** Code INSEE de la commune (utile pour les jointures futures). */
  inseeCode: string;
  /** Tous les codes postaux de la commune (utile pour l'affichage). */
  postalCodes: string[];
}

interface GeoApiCommune {
  nom: string;
  code: string;
  codesPostaux?: string[];
}

/** Commune de geo.api.gouv.fr → nom affiché + clé de ville (par son code INSEE). */
function villeDeLaCommune(c: GeoApiCommune): { name: string; slug: string } {
  const codes = { pays: "FR", codeInsee: c.code };
  const departement = departementDepuisCodes(codes);
  return {
    name: nomVilleAnnuaire({ ville: c.nom, pays: "FR", territoire: territoireDepuisCodes(codes) }),
    slug: slugVille({ pays: "FR", ville: c.nom, departement }) ?? "",
  };
}

const buildEndpoint = (query: string): string => {
  const trimmed = query.trim();
  // 5 chiffres = recherche par code postal (la plus rapide).
  if (/^\d{5}$/.test(trimmed)) {
    return `https://geo.api.gouv.fr/communes?codePostal=${trimmed}&fields=nom,code,codesPostaux&limit=10`;
  }
  // Sinon recherche par nom (boost score, limite côté API).
  return `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(trimmed)}&fields=nom,code,codesPostaux&boost=population&limit=10`;
};

/**
 * Choisit le code postal le plus pertinent : si la query est numérique
 * et matche un CP de la commune, on retourne celui-ci ; sinon le 1er.
 */
const pickPostalCode = (
  commune: GeoApiCommune,
  query: string,
): string => {
  const cps = commune.codesPostaux ?? [];
  if (cps.length === 0) return "";
  const q = query.trim();
  if (/^\d{2,5}$/.test(q)) {
    const match = cps.find((cp) => cp.startsWith(q));
    if (match) return match;
  }
  return cps[0] ?? "";
};

export const cityLookupApi = {
  /**
   * Cherche jusqu'à 10 villes par nom ou code postal. Retourne [] en cas
   * d'erreur ou si query < 2 chars (évite de spammer l'API au moindre
   * keystroke).
   */
  async search(query: string): Promise<CitySuggestion[]> {
    const trimmed = query.trim();
    if (!trimmed || trimmed.length < 2) return [];
    try {
      const res = await fetch(buildEndpoint(trimmed));
      if (!res.ok) return [];
      const data = (await res.json()) as GeoApiCommune[];
      return data.map((c) => ({
        ...villeDeLaCommune(c),
        postalCode: pickPostalCode(c, trimmed),
        inseeCode: c.code,
        postalCodes: c.codesPostaux ?? [],
      }));
    } catch {
      return [];
    }
  },

  /**
   * Reverse geocoding : à partir de coordonnées GPS, trouve la commune.
   * Endpoint : /communes?lat=X&lon=Y. Renvoie la 1re commune (la plus
   * proche) ou null.
   */
  async reverseGeocode(lat: number, lon: number): Promise<CitySuggestion | null> {
    try {
      const url = `https://geo.api.gouv.fr/communes?lat=${lat}&lon=${lon}&fields=nom,code,codesPostaux&limit=1`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const data = (await res.json()) as GeoApiCommune[];
      const c = data[0];
      if (!c) return null;
      return {
        ...villeDeLaCommune(c),
        postalCode: (c.codesPostaux ?? [])[0] ?? "",
        inseeCode: c.code,
        postalCodes: c.codesPostaux ?? [],
      };
    } catch {
      return null;
    }
  },
};
